<template>
	<BaseModal title="" v-model="model" noPadding>
		<div class="-mt-9 flex flex-col items-center px-5">
			<BaseCard class="relative flex h-40 w-40 items-center justify-center" alt>
				<svg
					width="160"
					height="160"
					viewBox="0 0 160 160"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
					class="absolute inset-0"
				>
					<mask
						id="mask0_1175_10568"
						style="mask-type: alpha"
						maskUnits="userSpaceOnUse"
						x="0"
						y="0"
						width="160"
						height="160"
					>
						<rect
							x="160"
							y="160"
							width="160"
							height="160"
							transform="rotate(180 160 160)"
							fill="url(#paint0_radial_1175_10568)"
						/>
					</mask>
					<g mask="url(#mask0_1175_10568)">
						<path
							opacity="0.1"
							d="M-288.185 5.87109H464.001V7.50684H-288.185V5.87109ZM-288.185 13.6201H464.001V15.2559H-288.185V13.6201ZM-288.185 21.3711H464.001V23.0068H-288.185V21.3711ZM-288.185 29.1201H464.001V30.7559H-288.185V29.1201ZM-288.185 36.8701H464.001V38.5059H-288.185V36.8701ZM-288.185 44.6201H464.001V46.2559H-288.185V44.6201ZM-288.185 52.3701H464.001V54.0059H-288.185V52.3701ZM-288.185 60.1191H464.001V61.7549H-288.185V60.1191ZM-288.185 67.8691H464.001V69.5049H-288.185V67.8691ZM-288.185 75.6191H464.001V77.2549H-288.185V75.6191ZM-288.185 83.3691H464.001V85.0049H-288.185V83.3691ZM-288.185 91.1182H464.001V92.7539H-288.185V91.1182ZM-288.185 98.8682H464.001V100.504H-288.185V98.8682ZM-288.185 106.618H464.001V108.254H-288.185V106.618ZM-288.185 114.368H464.001V116.004H-288.185V114.368ZM-288.185 122.117H464.001V123.753H-288.185V122.117ZM-288.185 129.867H464.001V131.503H-288.185V129.867ZM-288.185 137.617H464.001V139.253H-288.185V137.617ZM-288.185 145.368H464.001V147.004H-288.185V145.368ZM-288.185 153.117H464.001V154.753H-288.185V153.117Z"
							fill="#878787"
						/>
					</g>
					<defs>
						<radialGradient
							id="paint0_radial_1175_10568"
							cx="0"
							cy="0"
							r="1"
							gradientUnits="userSpaceOnUse"
							gradientTransform="translate(240 240) rotate(180) scale(80 79.7949)"
						>
							<stop offset="0.246979" stop-color="#0A1119" />
							<stop offset="1" stop-color="#33567F" stop-opacity="0" />
						</radialGradient>
					</defs>
				</svg>
				<LazySticker
					autoplay
					class="h-30 w-30"
					:src="animation(modelUuid)"
					:preloader="preloader || undefined"
					ref="sticker"
					@click="play"
					@error="onError()"
					v-if="!error"
				/>
				<LazyImage
					v-else
					class="h-30 w-30"
					:alt="modelName"
					:src="icon(modelUuid)"
					:preloader="preloader || undefined"
				/>
			</BaseCard>
			<div
				class="text-label-secondary mt-5 truncate text-center text-xs font-medium uppercase"
			>
				{{ collectionName }}
			</div>
			<div
				class="text-label-primary font-squad mt-2 truncate text-center text-2xl font-black uppercase"
			>
				{{ modelName }}
			</div>
			<div class="mt-5 flex justify-center gap-1.5">
				<BaseLabel alt>
					<div>{{ t('value') }}</div>
					<CoinPrice class="ml-1.5" size="xs" :price="price" />
				</BaseLabel>
				<BaseLabel alt>
					<div>{{ t('odds') }}</div>
					<div class="text-label-primary ml-1.5">{{ chance }}%</div>
				</BaseLabel>
			</div>
			<slot>
				<BenefitCard
					class="mt-7"
					:primary="ticketRange"
					:secondary="t('ticketsRange')"
				>
					<TicketLgIcon class="text-yellow" />
				</BenefitCard>
			</slot>
		</div>
		<BottomActionsModal class="mt-5 pt-4">
			<BaseButton size="lg" color="primary" class="w-full" @click="close()">
				{{ t('gotIt') }}
			</BaseButton>
		</BottomActionsModal>
	</BaseModal>
</template>
<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import BaseModal from './BaseModal.vue'
import BaseButton from '../BaseButton.vue'
import CoinPrice from '../CoinPrice.vue'
import BaseCard from '../BaseCard.vue'
import { useI18n } from 'vue-i18n'
import LazySticker from '../LazySticker.vue'
import BaseLabel from '../BaseLabel.vue'
import BenefitCard from '../BenefitCard.vue'
import TicketLgIcon from '@/assets/icons/ticket-lg.svg'
import { animation, icon } from '@/utils/urls'
import BottomActionsModal from '../BottomActionsModal.vue'
import LazyImage from '../LazyImage.vue'

const { t } = useI18n({
	messages: {
		en: {
			value: 'value',
			odds: 'odds',
			gotIt: 'got it',
			ticketsRange: 'tickets range'
		},
		ru: {
			value: 'цена',
			odds: 'шанс',
			gotIt: 'понятно',
			ticketsRange: 'промежуток билетов'
		}
	}
})

const props = defineProps<{
	preloader: string | null
	collectionName: string
	modelName: string
	modelUuid: string
	price: number
	chance: number
	ticketsRange: [number, number]
}>()

const model = defineModel<boolean>()

const close = () => {
	model.value = false
}

const ticketRange = computed(() => {
	const from = new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: 'USD',
		minimumFractionDigits: 0,
		maximumFractionDigits: 0
	})
		.formatToParts(props.ticketsRange[0])
		.filter(
			(part) =>
				part.type === 'integer' ||
				part.type === 'decimal' ||
				part.type === 'fraction' ||
				part.type === 'group'
		)
		.map((part) => part.value)
		.join('')

	const to = new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: 'USD',
		minimumFractionDigits: 0,
		maximumFractionDigits: 0
	})
		.formatToParts(props.ticketsRange[1])
		.filter(
			(part) =>
				part.type === 'integer' ||
				part.type === 'decimal' ||
				part.type === 'fraction' ||
				part.type === 'group'
		)
		.map((part) => part.value)
		.join('')

	return `${from} - ${to}`
})

const sticker = ref<InstanceType<typeof LazySticker>>()

const play = () => {
	if (!sticker.value) {
		return
	}

	sticker.value.play()
}

const error = ref(false)

const onError = () => {
	error.value = true
}

onBeforeUnmount(() => {
	error.value = false
})

watch([props, model], () => {
	error.value = false
})
</script>
